import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, HOME_FOR } from "@/context/AuthContext";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(HOME_FOR[res.user.role] || "/");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen">
      {/* left brand panel */}
      <div className="brand-hero relative hidden w-1/2 flex-col justify-between p-12 text-white lg:flex">
        <div className="brand-mesh absolute inset-0" />
        <Link to="/" className="relative inline-flex items-center rounded-lg bg-white px-3 py-2">
          <img src={GPA_LOGO} alt="GPA+" className="h-9 w-auto" />
        </Link>
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">
            The Transferable<br />Skills Benchmark
          </h2>
          <p className="mt-4 max-w-md text-slate-300">
            Sign in with your approved GPA+ account to continue to the private preview.
          </p>
        </div>
        <p className="relative text-xs text-slate-400">FERPA-aligned · Role-based access · No real student PII</p>
      </div>

      {/* right form */}
      <div className="flex w-full items-center justify-center bg-slate-50 px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#003057]">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Welcome back</h1>
          <p className="mt-1 text-slate-500">Sign in to continue to GPA+.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@lonestar.edu" className="mt-1.5" required />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs font-semibold text-[#005291] hover:text-[#78be21]">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" required />
            </div>
            {error && <p data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full rounded-full bg-[#003057] py-6 text-base font-bold hover:bg-[#004275]">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            New to GPA+?{" "}
            <Link to="/signup" data-testid="login-to-signup" className="font-semibold text-[#005291] hover:text-[#78be21]">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
